/**
 * A PRIMEIRA TELA DO DIA — quem tem jornada e nao bateu a entrada so ve esta.
 *
 * Decisao do Ruan (31/08/2026), depois de a CRISTINA abrir a gaveta dela as 08:01, lancar
 * 12 orcamentos e finalizar 5 vendas sem nenhuma batida no ponto: *"sem bater ponto nao
 * pode fazer nada, nem vender, tao pouco abrir caixa e fazer orcamentos. O ponto deve ser
 * a primeira acao do dia"*.
 *
 * ⛔ Isto NAO e' a trava — quem recusa `/api/*` inteiro e' o `exigirPontoDoDia` no
 * servidor, e o socket e' recusado no proprio handshake. Esta tela existe porque **403 no
 * lugar certo ainda e' uma tela errada** ([DA-543]): sem ela a pessoa cai no Dashboard e
 * ve destrocos.
 *
 * ⚠️ Mora FORA do `<Layout>`, como o `/balcao/pix`. Dentro do chassi as ~16 faixas de
 * alerta montariam e voltariam 403 cada uma — a tela de destrocos que este desvio evita.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { api } from '../../lib/api';
import { lerDestinoDoPonto, limparDestinoDoPonto } from '../../lib/ponto-bloqueio';
import { PontoRegistrar } from './PontoRegistrar';

/** De quanto em quanto tempo a tela pergunta se a entrada ja foi batida em outra aba. */
const INTERVALO_CONFERE_MS = 6_000;

export function PontoEntradaDoDia() {
  const { usuario, loading, logout } = useAuth();
  const navigate = useNavigate();

  // Resolvido UMA vez, na montagem: o `document.referrer` so vale enquanto ninguem
  // navegou por cima, e a tela nao pode perder o destino num re-render.
  const destinoRef = useRef<string>(lerDestinoDoPonto());

  /**
   * Sai desta tela devolvendo a pessoa pra onde ela estava.
   *
   * ⚠️ `window.location.href` e nao `navigate`, pelo motivo que ja valia aqui: a recarga
   * rele o `/me` (que no estado atual ainda diz `ponto_pendente: true`) e refaz a conexao
   * do socket, recusada em DEFINITIVO no handshake. Com `navigate` a pessoa entraria no
   * sistema com o tempo real morto — e, pior, o `ExigirPontoDoDia` a devolveria pra ca,
   * porque o `ponto_pendente` do contexto so muda relendo o `/me`.
   */
  const voltarPraOndeEstava = useCallback(() => {
    const destino = destinoRef.current;
    limparDestinoDoPonto();
    window.location.href = destino;
  }, []);

  // Quem NAO esta travado nao tem o que fazer aqui — cai pro sistema normal. Cobre o
  // endereco digitado a mao e a volta do historico depois de bater o ponto.
  useEffect(() => {
    if (!loading && usuario && !usuario.ponto_pendente) navigate('/', { replace: true });
  }, [loading, usuario, navigate]);

  /**
   * A ENTRADA PODE TER SIDO BATIDA EM OUTRA ABA — e ate 11/09/2026 esta tela nao tinha
   * como saber ([DA-715](../../../../docs/decisoes-arquitetura.md)).
   *
   * Ela lia o dia uma vez, na montagem, e ficava. Quem tinha varias abas abertas batia a
   * entrada numa e as outras seguiam mostrando *"registre sua entrada"* — **com o botao
   * ativo**. Medido no `access.log` em 11/09: `POST /api/ponto/entrada` 200 as 12:58:38 e
   * **dois 400** as 12:58:50 e 12:58:52, que sao os cliques nas abas presas. A tela
   * oferecia o que o servidor ja ia negar, e a saida era fechar e abrir o navegador.
   *
   * ⚠️ `/api/ponto/dia` e' isenta da trava (`PREFIXOS_ISENTOS`), senao a pergunta seria
   * recusada pela mesma trava que ela observa. `proxima_acao === 'saida'` significa
   * presenca VIVA — e' a regra unica do `ponto-presenca.ts`, nao um segundo criterio.
   */
  const conferirSeJaEntrou = useCallback(async () => {
    try {
      const dia = await api.get<{ proxima_acao: 'entrada' | 'saida' }>('/api/ponto/dia');
      if (dia.proxima_acao === 'saida') voltarPraOndeEstava();
    } catch {
      // Falha de rede nao muda nada: quem libera e' a entrada batida, e o proximo tique
      // pergunta de novo.
    }
  }, [voltarPraOndeEstava]);

  useEffect(() => {
    const id = window.setInterval(() => void conferirSeJaEntrou(), INTERVALO_CONFERE_MS);
    // O navegador estrangula timer de aba em segundo plano, e a aba que ela volta a olhar
    // e' justamente a que precisa responder na hora.
    function aoVoltarPraAba() {
      if (document.visibilityState === 'visible') void conferirSeJaEntrou();
    }
    document.addEventListener('visibilitychange', aoVoltarPraAba);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', aoVoltarPraAba);
    };
  }, [conferirSeJaEntrou]);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--page-bg)', color: 'var(--text)' }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px 48px' }}>
        <header style={{ marginBottom: 20 }}>
          {/* Padrao de titulo da casa mesmo fora do Layout: o `.pagina-titulo` nao e' so
              estetica — sem ele o texto cai pra 14px no preflight do Tailwind e o
              `useDocumentTitle` deixa a aba do navegador so com "FIODECOBRE". */}
          {/* ⚠️ Nada de "Bom dia" fixo: com presenca VIVA esta tela tambem aparece depois
              da saida do almoco e da saida das 18h, e uma saudacao de manha aqui seria a
              tela afirmando uma hora que ninguem checou. */}
          {/*
            ⛔ SAIDA DE EMERGENCIA, e ela mora NO CABECALHO de proposito. Sem ela, quem nao
            consegue bater — camera negada, rosto que nao casa no threshold, celular sem
            permissao — fica preso numa tela sem nenhum botao que responda, com a sessao
            aberta; e num aparelho compartilhado (o balcao) a maquina trava no usuario que
            abriu. O bypass com motivo continua sendo o caminho normal (esta no card
            abaixo); este botao e' pra quando nem ele resolve, e pra trocar de conta.
            ⚠️ Ficava DEPOIS do card e caia fora da dobra numa tela de 1030px — botao de
            emergencia que exige rolagem nao existe pra quem esta com cliente no balcao
            (relato do Ruan, 01/09/2026). No cabecalho ele aparece sem rolar nada.
          */}
          <div className="pagina-header">
            <h2 className="pagina-titulo">
              {usuario?.nome?.split(' ')[0] ?? 'Ola'}, registre sua entrada para usar o sistema
            </h2>
            <div className="pagina-acoes">
              <button
                type="button"
                onClick={() => { void logout(); }}
                title="Sair da conta para outra pessoa entrar neste aparelho"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                  background: 'transparent', color: 'inherit',
                  border: '1px solid currentColor', borderRadius: 6, opacity: 0.9,
                }}
              >
                <LogOut size={15} />
                Sair
              </button>
            </div>
          </div>
          {/*
            ⚠️ O texto diz o que esta travado e ate quando — nunca so "sem permissao".
            Botao (ou tela) que barra sem dizer por que e' beco sem saida, e aqui a pessoa
            esta com cliente no balcao: ela precisa entender em uma linha que o caminho e'
            bater o ponto, nao chamar alguem.
          */}
          <p style={{ fontSize: 14, opacity: 0.8, margin: 0, lineHeight: 1.5 }}>
            O ponto e a primeira acao do dia. Enquanto a entrada nao estiver aberta, o
            sistema fica bloqueado — nao da pra vender, abrir o caixa nem lancar orcamento.
            Assim que voce bater, tudo abre. Se voce acabou de registrar a saida, esta tela
            e' o esperado: bata a entrada de novo quando voltar.
          </p>
        </header>

        {/*
          Bateu a entrada AQUI: volta pra tela de onde a pessoa veio, nao pro Dashboard
          (o porque da recarga inteira esta no `voltarPraOndeEstava`, acima).
        */}
        <PontoRegistrar aoRegistrar={(tipo) => { if (tipo === 'entrada') voltarPraOndeEstava(); }} />
      </div>
    </div>
  );
}
